import { useAccount } from "@azure/msal-react";
import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Card,
    Flex,
    Group,
    rem,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    Title,
    useMantineColorScheme,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useLocalStorage } from "@uidotdev/usehooks";
import type { IndexableType } from "dexie";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useRef, useState } from "react";
import { AiOutlineSend } from "react-icons/ai";
import { IoStopCircle } from "react-icons/io5";
import { FollowUpSuggestions } from "src/components/FollowUpSuggestions";
import { MessageItem } from "src/components/MessageItem";
import { sendFeedbackToApi } from "src/components/MessageUtils";
import { db, type MessageEntity } from "src/db";
import {
    abortCurrentStream,
    useApiStreamingState,
    useChatCompletion,
} from "src/hooks/useChatCompletion";
import { useMetadata } from "src/hooks/useMetadata";
import {
    type CopilotContextData,
    getContextualSuggestions as getContextSuggestions,
} from "src/utils/contextTemplates";

const APP_TITLE = import.meta.env.VITE_APP_TITLE ?? "Knowledge Search";

/**
 * Convert context tags to CopilotContextData for template generation
 */
function contextTagsToContextData(contextTags?: ContextTag[]): CopilotContextData | undefined {
    if (!contextTags || contextTags.length === 0) {
        return undefined;
    }

    const context: CopilotContextData = {};

    for (const tag of contextTags) {
        switch (tag.type) {
            case "equipment":
                context.equipmentId = tag.value;
                break;
            case "alert":
                context.alertDescription = tag.value;
                break;
            case "location":
                context.location = tag.value;
                break;
            default:
                // Handle additional tags like "Type"
                if (tag.label === "Type") {
                    context.equipmentType = tag.value;
                }
        }
    }

    return context;
}

/**
 * Generate context-aware suggestions based on the provided context tags
 */
function getContextualSuggestions(contextTags?: ContextTag[]): string[] {
    const contextData = contextTagsToContextData(contextTags);
    return getContextSuggestions(contextData);
}

/** Context tag to display near the input */
export interface ContextTag {
    label: string;
    value: string;
    type?: "equipment" | "alert" | "location" | "default";
}

export function ChatRouteChild({
    chatId,
    messages,
    files,
    isCompact,
    isChatWithDocument,
    metadataFilter,
    customStyles,
    hideHeader,
    initialContent,
    onContentChange,
    contextTags,
    onClearContext,
    onChatCreated,
    disabled,
}: {
    readonly chatId?: IndexableType;
    readonly messages?: MessageEntity[];
    readonly files?: string[];
    readonly isCompact?: boolean;
    readonly isChatWithDocument?: boolean;
    readonly metadataFilter?: { key: string; value: string }[];
    readonly customStyles?: Record<string, string | number>;
    readonly hideHeader?: boolean;
    /** Initial content to pre-fill in the input field */
    readonly initialContent?: string;
    /** Callback when content changes (for controlled input from parent) */
    readonly onContentChange?: (content: string) => void;
    /** Context tags to display near the input */
    readonly contextTags?: ContextTag[];
    /** Callback when a context tag is removed (receives label) or all context is cleared */
    readonly onClearContext?: (label?: string) => void;
    /** Callback when a new chat is created (returns the new chat ID) */
    readonly onChatCreated?: (chatId: string) => void;
    /** Disable input (e.g., while history is loading) */
    readonly disabled?: boolean;
}) {
    const navigate = useNavigate();
    const { chatMetadata, addChatMetadata } = useMetadata();
    const account = useAccount();
    const { colorScheme } = useMantineColorScheme();

    // User preferences from local storage
    const [isForbidden, _] = useLocalStorage("isForbidden", false);
    const [chatPreferences, _setChatPreferences] = useLocalStorage<{
        selectedProductLines: string[];
        selectedDatasources: string[];
    }>("chatPreferences", {
        selectedProductLines: [],
        selectedDatasources: [],
    });
    const [selectedOrganization, _setSelectedOrganization] = useLocalStorage<string>(
        "selectedOrganization",
        "",
    );
    // Check if preferences exist and navigate to questionnaire if needed
    // Skip questionnaire redirect in copilot mode (embedded in iframe)
    const isCopilotMode =
        window.location.hash.includes("/copilot") ||
        window.location.search.includes("mode=copilot");

    const reports =
        JSON.parse(localStorage.getItem("datasourceDetails") || "[]")
            .filter((datasource) => datasource?.enable_chat_with_document)
            .map((datasource) => datasource.id) || [];

    useEffect(() => {
        if (!chatPreferences.selectedProductLines.length && !isCopilotMode) {
            navigate({
                to: "/questionnaire",
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                search: { source: document.location.pathname.replace(/\/{2,}/g, "/") } as any,
            });
        }
    }, [chatPreferences, navigate, isCopilotMode]);

    useEffect(() => {
        if (files && files?.length > 0) {
            for (const file of files) {
                if (
                    chatMetadata.find((metadata) => {
                        return reports.includes(metadata.key) && file.startsWith(metadata.value);
                    })
                )
                    return;
                addChatMetadata(file.split("_")[0], "file");
            }
        }
    }, [files, chatMetadata, addChatMetadata, reports]);
    const chatCompletion = useChatCompletion(chatId, isChatWithDocument);
    const [isInitialLoading, _setIsInitialLoading] = useState(false);
    const isStreaming = useApiStreamingState(chatId?.toString());
    const isLoading = chatCompletion.isloading || isInitialLoading || isStreaming;

    const [content, setContent] = useState(initialContent || "");
    const ref = useRef<HTMLTextAreaElement>(null);

    // Store callback in ref to avoid triggering effects when callback reference changes
    // This prevents React Error #185 (Maximum update depth exceeded) when the parent
    // component recreates the callback on state changes
    const onContentChangeRef = useRef(onContentChange);
    useEffect(() => {
        onContentChangeRef.current = onContentChange;
    }, [onContentChange]);

    // Update content when initialContent prop changes (for copilot suggested questions)
    useEffect(() => {
        if (initialContent) {
            setContent((prevContent) => {
                if (prevContent !== initialContent) {
                    // Focus the input and move cursor to end
                    if (ref.current) {
                        ref.current.focus();
                        ref.current.setSelectionRange(initialContent.length, initialContent.length);
                    }
                    return initialContent;
                }
                return prevContent;
            });
        }
    }, [initialContent]);

    // Notify parent when content changes (using ref to prevent infinite loops)
    useEffect(() => {
        onContentChangeRef.current?.(content);
    }, [content]);

    const [selectedQuestions, setSelectedQuestions] = useState<string[]>(
        isChatWithDocument ? [] : getContextualSuggestions(contextTags),
    );
    const chat = useLiveQuery(
        () => (chatId == null ? undefined : db.chats.where("id").equals(chatId).first()),
        [chatId],
    );
    useEffect(() => {
        if (chatId == null) return;

        const loadMessages = async () => {
            const suggestedMessages = chat?.suggestedMessages ?? [];
            setSelectedQuestions(suggestedMessages);
        };

        loadMessages();
    }, [chatId, chat]);

    // Update suggestions when context tags change
    useEffect(() => {
        // Only update if there's no active chat (new chat state)
        if (!chatId && !isChatWithDocument) {
            setSelectedQuestions(getContextualSuggestions(contextTags));
        }
    }, [contextTags, chatId, isChatWithDocument]);

    /**
     * Build a context-enriched message that includes context information
     * This allows the backend/LLM to understand the equipment context
     * Context is appended at the END so it doesn't appear in message previews
     */
    const buildContextEnrichedMessage = (userMessage: string, tags?: ContextTag[]): string => {
        if (!tags || tags.length === 0) {
            return userMessage;
        }

        // Build context section
        const contextParts: string[] = [];
        for (const tag of tags) {
            contextParts.push(`${tag.label}: ${tag.value}`);
        }

        // Format: User question first, then context at the end
        // This way the message preview shows the actual question, not the context
        const contextSuffix = `\n\n[Context: ${contextParts.join(", ")}]`;
        return userMessage + contextSuffix;
    };

    const submit = async () => {
        const newContent = content;
        setContent("");
        setSelectedQuestions([]);

        // Enrich message with context if available
        const enrichedMessage = buildContextEnrichedMessage(newContent, contextTags);

        await chatCompletion.sendMessage(
            enrichedMessage,
            chatId,
            metadataFilter,
            // Callback for when a new chat is created (called before streaming starts)
            !chatId && onChatCreated
                ? (newChatId) => onChatCreated(newChatId.toString())
                : undefined,
        );
    };

    // Use either isCompact or isChatWithDocument for compact mode
    const isCompactMode = isCompact || isChatWithDocument;

    const [titleDisplay, setTitleDisplay] = useState(APP_TITLE);

    useEffect(() => {
        try {
            let organizationName = selectedOrganization;
            if (!organizationName || organizationName === "all") {
                organizationName = "Default Filters";
            }

            const baseTitle = APP_TITLE;
            const newTitle =
                organizationName === "Default Filters"
                    ? baseTitle
                    : `${baseTitle} - ${organizationName}`;

            setTitleDisplay(newTitle);
            document.title = newTitle;
        } catch (error) {
            console.error("Error updating chat title:", error);
        }
    }, [selectedOrganization]);

    const scrollRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: messages?.length || 0,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 150,
        overscan: 5,
    });

    useEffect(() => {
        if (!scrollRef.current || !messages?.length) return;

        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages?.length]);

    return (
        <Flex
            direction="column"
            style={{
                height: isCompact ? "100vh" : "calc(100vh - 100px)", // No header in compact mode
                position: "relative",
                paddingBottom: isCompact ? "80px" : "100px",
            }}
        >
            {/* Scrollable content area (title + messages) */}
            <Box
                ref={scrollRef}
                style={{
                    flexGrow: 1,
                    overflow: "auto",
                    marginBottom: isCompact ? rem(100) : rem(80),
                    padding: isCompact ? "12px" : undefined,
                }}
            >
                {!isChatWithDocument && !hideHeader && (
                    <Title order={3} mb={16} pl="xl" c="purple.8">
                        <Flex align="center">
                            <Title>{titleDisplay}</Title>
                            {!isForbidden && (
                                <Button
                                    variant="outline"
                                    size="xs"
                                    ml={8}
                                    onClick={() =>
                                        navigate({
                                            to: "/questionnaire",
                                            // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                                            search: { source: document.location.pathname } as any,
                                        })
                                    }
                                >
                                    Edit Preferences
                                </Button>
                            )}
                        </Flex>
                    </Title>
                )}

                {/* Messages Area */}
                <Box
                    ref={messagesContainerRef}
                    style={(theme) => ({
                        padding: isCompactMode ? "0" : `0 ${theme.spacing.xl}`,
                        paddingBottom: isCompactMode ? rem(40) : theme.spacing.md,
                    })}
                >
                    {messages && messages.length > 0 ? (
                        <div
                            style={{
                                height: `${virtualizer.getTotalSize()}px`,
                                width: "100%",
                                position: "relative",
                            }}
                        >
                            {virtualizer.getVirtualItems().map((virtualRow, _index) => {
                                const message = messages[virtualRow.index];
                                const isLastMessage = virtualRow.index === messages.length - 1;
                                const isAssistantMessage = message.role === "assistant";
                                const showStreaming =
                                    isStreaming && isLastMessage && isAssistantMessage;

                                return (
                                    <Stack
                                        key={message.id.toString()}
                                        ref={virtualizer.measureElement}
                                        data-index={virtualRow.index}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            transform: `translateY(${virtualRow.start}px)`,
                                            willChange: showStreaming ? "contents" : "transform",
                                        }}
                                        gap="xs"
                                    >
                                        <MessageItem
                                            isCompact={isCompactMode}
                                            sendFeedbackToApi={sendFeedbackToApi}
                                            retry={async (newMessage: string) => {
                                                setContent("");
                                                setSelectedQuestions([]);
                                                // Note: retry uses the original message which may already have context
                                                await chatCompletion.sendMessage(
                                                    newMessage,
                                                    chatId,
                                                    metadataFilter,
                                                );
                                            }}
                                            message={message}
                                            nextAssistantMessage={
                                                messages[virtualRow.index + 1] &&
                                                virtualRow.index > 1
                                                    ? messages[virtualRow.index + 1]
                                                    : undefined
                                            }
                                            inProgress={showStreaming}
                                            followUpSuggestions={undefined}
                                            isLastMessage={false}
                                        />
                                    </Stack>
                                );
                            })}
                        </div>
                    ) : (
                        <Flex
                            direction="column"
                            align="center"
                            justify="center"
                            h="100%"
                            style={{ opacity: 0.7, marginTop: isCompact ? 40 : 0 }}
                        >
                            <Text size="xl" fw={500} c="black">
                                Start a conversation
                            </Text>
                            <Text size="md" c="black" ta="center" mt="sm">
                                Choose one of the suggested questions below or type your own
                            </Text>
                        </Flex>
                    )}

                    {/* Render suggestions outside the virtual container to avoid clipping */}
                    {messages && messages.length > 0 && selectedQuestions.length > 0 && (
                        <Box
                            style={(theme) => ({
                                align: "center",
                                padding: isCompactMode ? "0" : `0 ${theme.spacing.xl}`,
                                paddingBottom: isCompactMode ? rem(24) : undefined,
                            })}
                        >
                            <FollowUpSuggestions
                                suggestions={selectedQuestions}
                                onSuggestionClick={(suggestion: string) => {
                                    setContent("");
                                    setSelectedQuestions([]);
                                    // Enrich suggestion with context if available
                                    const enrichedSuggestion = buildContextEnrichedMessage(
                                        suggestion,
                                        contextTags,
                                    );
                                    chatCompletion.sendMessage(
                                        enrichedSuggestion,
                                        chatId,
                                        metadataFilter,
                                    );
                                }}
                                maxSuggestions={isCompactMode ? 1 : undefined}
                                compact={isCompactMode}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Sticky Input Area */}
            <Box
                py="lg"
                px="xl"
                style={(theme) => ({
                    bottom: 0,
                    left: 0,
                    right: 0,
                    position: "absolute",
                    backgroundColor:
                        colorScheme === "dark" ? theme.colors.dark[9] : theme.colors.gray[0],
                    boxShadow: "none",
                    zIndex: 10,
                    ...(customStyles
                        ? customStyles
                        : {
                              [`@media (minWidth: ${theme.breakpoints.md})`]: {
                                  left: rem(200),
                              },
                              [`@media (minWidth: ${theme.breakpoints.lg})`]: {
                                  left: rem(300),
                              },
                          }),
                })}
            >
                <Stack gap="0">
                    {/* Only show suggestions if there are no messages (initial state) */}
                    {(!messages || messages.length === 0) && (
                        <SimpleGrid cols={2} spacing="sm" style={{ maxWidth: "100%" }} mb="sm">
                            {selectedQuestions.map((question) => (
                                <Card
                                    shadow="sm"
                                    padding="lg"
                                    radius="md"
                                    withBorder
                                    key={question}
                                    variant="outline"
                                    style={{
                                        cursor: "pointer",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        textAlign: "left",
                                    }}
                                    onClick={() => {
                                        if (!isForbidden) {
                                            setContent(question);
                                        }
                                    }}
                                >
                                    {question}
                                </Card>
                            ))}
                        </SimpleGrid>
                    )}

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (chatCompletion.isloading || isLoading || isForbidden || disabled)
                                return false;
                            submit();
                            (document.activeElement as HTMLElement)?.blur();
                            return false;
                        }}
                    >
                        {/* Context pills - shown when host app sets context */}
                        {contextTags && contextTags.length > 0 && (
                            <Group gap="xs" mb="xs">
                                {contextTags.map((tag) => (
                                    <Badge
                                        key={`${tag.label}-${tag.value}`}
                                        size="sm"
                                        variant="light"
                                        color={
                                            tag.type === "equipment"
                                                ? "purple"
                                                : tag.type === "alert"
                                                  ? "red"
                                                  : tag.type === "location"
                                                    ? "blue"
                                                    : "gray"
                                        }
                                        rightSection={
                                            onClearContext && (
                                                <ActionIcon
                                                    size="xs"
                                                    variant="transparent"
                                                    color="gray"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onClearContext(tag.label);
                                                    }}
                                                    style={{ marginLeft: 2 }}
                                                >
                                                    <IconX size={10} />
                                                </ActionIcon>
                                            )
                                        }
                                        styles={{
                                            root: {
                                                paddingRight: onClearContext ? 4 : undefined,
                                            },
                                        }}
                                    >
                                        <Text size="xs" fw={500} span>
                                            {tag.label}:
                                        </Text>{" "}
                                        {tag.value}
                                    </Badge>
                                ))}
                            </Group>
                        )}

                        {isCompact ? (
                            /* Compact mode: textarea with absolute positioned button */
                            <Box style={{ position: "relative", width: "100%" }}>
                                <Textarea
                                    disabled={isForbidden || disabled}
                                    ref={ref}
                                    value={content}
                                    style={{ width: "100%" }}
                                    placeholder={
                                        disabled
                                            ? "Loading chat history..."
                                            : "How can I help you today?"
                                    }
                                    id="chat-input"
                                    autosize
                                    minRows={2}
                                    maxRows={6}
                                    onChange={(event) => setContent(event.currentTarget.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" && !event.shiftKey) {
                                            event.preventDefault();
                                            if (
                                                !chatCompletion.isloading &&
                                                !isLoading &&
                                                !isForbidden &&
                                                !disabled &&
                                                content.trim()
                                            ) {
                                                submit();
                                            }
                                        }
                                    }}
                                    styles={(theme) => ({
                                        input: {
                                            borderRadius: theme.radius.sm,
                                            paddingRight: rem(50),
                                            paddingBottom: rem(8),
                                            fontSize: rem(13),
                                        },
                                    })}
                                />
                                <Button
                                    aria-label={isStreaming ? "Stop" : "Submit"}
                                    data-testid={isStreaming ? "stop-button" : "send-button"}
                                    variant="filled"
                                    color={isStreaming ? "red" : undefined}
                                    onClick={isStreaming ? abortCurrentStream : submit}
                                    disabled={
                                        !isStreaming &&
                                        (chatCompletion.isloading ||
                                            isLoading ||
                                            !content ||
                                            isForbidden ||
                                            disabled)
                                    }
                                    style={{
                                        position: "absolute",
                                        bottom: rem(8),
                                        right: rem(8),
                                        fontSize: "1rem",
                                        minWidth: rem(32),
                                        height: rem(32),
                                        padding: 0,
                                    }}
                                >
                                    {isStreaming ? (
                                        <IoStopCircle size={18} />
                                    ) : (
                                        <AiOutlineSend size={16} />
                                    )}
                                </Button>
                            </Box>
                        ) : (
                            /* Main app: original side-by-side layout */
                            <Flex gap="sm" align="center" style={{ position: "relative" }}>
                                <Flex style={{ flex: 1 }}>
                                    <Textarea
                                        disabled={isForbidden || disabled}
                                        ref={ref}
                                        value={content}
                                        style={{ flex: 1 }}
                                        placeholder={`Hello, ${account?.name || "there"}!
How can I assist with your technical work today? Please share the full context for a precise response.
The more details you provide, the better I can assist you.`}
                                        id="chat-input"
                                        autosize
                                        minRows={4}
                                        maxRows={8}
                                        onChange={(event) => setContent(event.currentTarget.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" && !event.shiftKey) {
                                                event.preventDefault();
                                                if (
                                                    !chatCompletion.isloading &&
                                                    !isLoading &&
                                                    !isForbidden &&
                                                    !disabled &&
                                                    content.trim()
                                                ) {
                                                    submit();
                                                }
                                            }
                                        }}
                                        styles={(theme) => ({
                                            input: {
                                                borderRadius: theme.radius.sm,
                                            },
                                        })}
                                    />
                                </Flex>
                                <Button
                                    aria-label={isStreaming ? "Stop" : "Submit"}
                                    data-testid={isStreaming ? "stop-button" : "send-button"}
                                    variant="filled"
                                    color={isStreaming ? "red" : undefined}
                                    onClick={isStreaming ? abortCurrentStream : submit}
                                    disabled={
                                        !isStreaming &&
                                        (chatCompletion.isloading ||
                                            isLoading ||
                                            !content ||
                                            isForbidden ||
                                            disabled)
                                    }
                                    style={{
                                        fontSize: "1.2rem",
                                    }}
                                >
                                    {isStreaming ? <IoStopCircle size={20} /> : <AiOutlineSend />}
                                </Button>
                            </Flex>
                        )}
                    </form>
                    <Text
                        fw="bold"
                        c="gray.7"
                        mt=".5rem"
                        fz="xs"
                        ta="center"
                        style={{
                            maxWidth: "80%",
                            margin: "0 auto",
                            "@media (maxWidth: 768px)": {
                                fontSize: "10px",
                                lineHeight: 1.2,
                                marginTop: "0.25rem",
                            },
                        }}
                    >
                        Knowledge Search uses Generative AI. Please verify the answers for accuracy
                        before using them.
                    </Text>
                </Stack>
            </Box>
        </Flex>
    );
}
